import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const HeroSection = ({ onLoginClick }) => {
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const langMenuRef = useRef(null);

  const languages = [
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'bs', name: 'Bosanski', flag: '🇧🇦' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  // Automatska detekcija jezika
  useEffect(() => {
    const detectLocation = async () => {
      const hasSetLanguageManual = localStorage.getItem('i18nextLngManual');
      if (hasSetLanguageManual) return;

      try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) throw new Error('Mreža nije dostupna');
        
        const data = await response.json();
        if (!data || !data.country_code) return;

        const country = data.country_code;
        let detectedLang = 'en';

        if (country === 'IT') {
          detectedLang = 'it';
        } else if (['BA', 'RS', 'HR', 'ME'].includes(country)) {
          detectedLang = 'bs';
        } else if (['DE', 'AT', 'CH'].includes(country)) {
          detectedLang = 'de';
        } else {
          detectedLang = 'en';
        }

        if (i18n.language !== detectedLang) {
          i18n.changeLanguage(detectedLang);
        }
      } catch (error) {
        console.error("Automatska detekcija lokacije nije uspjela:", error);
      }
    };

    detectLocation();
  }, [i18n]);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('i18nextLngManual', 'true'); 
    setShowLangMenu(false);
  };

  return (
    <section className="w-full min-h-[80vh] flex flex-col items-center justify-center px-4 md:px-6 lg:px-8 py-12">
      <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
        
        {/* Logotip */}
        <div className="flex flex-col items-center justify-center mb-16 md:mb-24 animate-fade-in">
          <div className="relative flex items-center justify-center">
            <Icon 
              name="Shield" 
              size={56} 
              color="var(--color-primary)" 
              className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20" 
              strokeWidth={1.6}
            />
            <span className="absolute inset-0 flex items-center justify-center text-primary font-black text-2xl md:text-3xl lg:text-4xl select-none" style={{ transform: 'translateY(-5%)' }}>
              O
            </span>
          </div>
          <span className="text-primary font-bold uppercase tracking-widest text-xs md:text-sm mt-3 select-none">
            Desk
          </span>
        </div>

        {/* Tekstualni dio */}
        <div className="space-y-4 md:space-y-6 mb-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight tracking-tight">
            {t('hero.welcome')} <span className="text-primary">OrizonDesk</span>
          </h1>
        </div>

        {/* Dugme za Login */}
        <div className="w-full flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={onLoginClick}
            className="w-full sm:w-auto min-w-[280px] flex items-center justify-center gap-3 px-10 py-5 bg-primary text-primary-foreground rounded-2xl hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
          >
            <Icon name="LogIn" size={24} color="currentColor" />
            <span className="text-xl font-bold">{t('hero.cta_button')}</span>
          </button>
        </div>

        {/* Zastave - DODATNO SPUŠTENE na mt-40/48 */}
        <div className="relative mt-40 md:mt-48 pb-10" ref={langMenuRef}>
          <button 
            type="button"
            onClick={() => setShowLangMenu(!showLangMenu)}
            className="flex items-center gap-2 px-5 py-2.5 bg-card border border-border rounded-xl hover:bg-muted transition-all shadow-sm"
          >
            <span className="text-2xl">{currentLang.flag}</span>
            <span className="text-sm font-medium">{currentLang.name}</span>
            <Icon name="ChevronDown" size={14} className={`transition-transform duration-300 ${showLangMenu ? 'rotate-180' : ''}`} />
          </button>

          {showLangMenu && (
            <div className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[1000] min-w-[180px] py-2 overflow-hidden animate-in fade-in slide-in-from-bottom-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-100 transition-colors ${i18n.language === lang.code ? 'bg-primary/5 text-primary font-bold' : 'text-gray-700'}`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <span className="text-sm">{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export default HeroSection;